import React, { useRef, useState, useEffect } from 'react';

interface TakePictureProps {
    component: any;
    value: any;
    onChange?: (value: any) => void;
    disabled?: boolean;
}

const TakePicture: React.FC<TakePictureProps> = ({ value, onChange, disabled }) => {
    const [image, setImage] = useState<string | null>(typeof value === 'string' ? value : null);
    const [isCameraOpen, setIsCameraOpen] = useState(false);
    const [stream, setStream] = useState<MediaStream | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        if (value && typeof value === 'string') {
            setImage(value);
        }
    }, [value]);

    const startCamera = async () => {
        try {
            if (stream) {
                stream.getTracks().forEach(track => track.stop());
            }
            setIsCameraOpen(true);
            const newStream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: facingMode }
            });
            setStream(newStream);
            if (videoRef.current) {
                videoRef.current.srcObject = newStream;
            }
        } catch (err) {
            console.error("Error accessing camera", err);
            alert("Could not access camera. Please check permissions.");
            setIsCameraOpen(false);
        }
    };

    const stopCamera = () => {
        if (stream) {
            stream.getTracks().forEach(track => track.stop());
            setStream(null);
        }
        setIsCameraOpen(false);
    };

    const capture = () => {
        if (videoRef.current && canvasRef.current) {
            const video = videoRef.current;
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            if (context) {
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                const dataUrl = canvas.toDataURL('image/jpeg');
                setImage(dataUrl);
                if (onChange) onChange(dataUrl);
                stopCamera();
            }
        }
    };

    const retake = () => {
        setImage(null);
        if (onChange) onChange(null);
        startCamera();
    };

    // const switchCamera = () => {
    //     setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
    //     // Effect dependency on facingMode isn't automatic for startCamera, so we restart explicitly
    //     // But triggering re-render with new state will not auto-call startCamera.
    //     // We must call it after state update or rely on useEffect if we structured it that way.
    //     // Simplest: close and reopen or just recall.
    //     // But setState is async.
    //     // Let's use specific function flow:
    //     if (stream) {
    //         stream.getTracks().forEach(track => track.stop());
    //     }
    //     const newMode = facingMode === 'user' ? 'environment' : 'user';
    //     // We can't use 'facingMode' state immediately here as it's stale.
    //     // Wait for re-render? Or pass explicitly.
    //     // Let's rely on re-clicking open or just handling it simpler in useEffect[facingMode]?
    //     // But we don't want to auto-open on mount.
    // };

    // Effect to restart camera if mode changes AND camera is already open
    useEffect(() => {
        if (isCameraOpen) {
            startCamera();
        }
    }, [facingMode]);

    return (
        <div className="flex flex-col items-center gap-4 p-4 border rounded bg-gray-50 max-w-md mx-auto">
            {/* Hidden Canvas */}
            <canvas ref={canvasRef} className="hidden" />

            {image ? (
                <div className="flex flex-col items-center gap-2 w-full">
                    <img src={image} alt="Captured" className="w-full rounded border shadow-sm" />
                    {!disabled && (
                        <button
                            onClick={retake}
                            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        >
                            Retake Photo
                        </button>
                    )}
                </div>
            ) : (
                <div className="flex flex-col items-center gap-2 w-full relative bg-black rounded overflow-hidden aspect-video justify-center">
                    {isCameraOpen ? (
                        <>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute bottom-4 flex gap-4">
                                <button
                                    onClick={() => setFacingMode(prev => prev === 'user' ? 'environment' : 'user')}
                                    className="p-2 bg-white/20 backdrop-blur rounded-full text-white hover:bg-white/40"
                                    title="Switch Camera"
                                >
                                    🔄
                                </button>
                                <button
                                    onClick={capture}
                                    className="w-16 h-16 bg-white rounded-full border-4 border-gray-300 shadow-lg hover:bg-gray-100 flex items-center justify-center p-1"
                                >
                                    <div className="w-12 h-12 bg-red-500 rounded-full"></div>
                                </button>
                                <button
                                    onClick={stopCamera}
                                    className="p-2 bg-white/20 backdrop-blur rounded-full text-white hover:bg-white/40"
                                    title="Close Camera"
                                >
                                    ❌
                                </button>
                            </div>
                        </>
                    ) : (
                        <button
                            onClick={startCamera}
                            disabled={disabled}
                            className="px-6 py-3 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition-colors flex items-center gap-2"
                        >
                            📷 Open Camera
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default TakePicture;
